import { Popover, PopoverContent, PopoverTrigger } from '@navioworks/ui';
import { AlertTriangle, CheckCircle2, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { displayErrorMessage } from '@/lib/error-display';
import { formatDateTime, formatTime } from '@/lib/format';
import { getOfflineActorUserId } from '@/lib/offline-actor';
import {
  approveOfflineOperationForSync,
  retryOfflineOperation,
} from '@/lib/offline-queue-review';
import {
  listOfflineQueuedOperations,
  removeOfflineOperation,
  subscribeToOfflineStore,
  type OfflineQueueRecord,
} from '@/lib/offline-store';
import { syncNavioBuildOfflineQueue } from '@/lib/offline-sync';

type QueueAction = 'approve' | 'discard' | 'retry' | 'sync';

interface OfflineQueuePanelProps {
  children: ReactNode;
  isOnline: boolean;
}

interface OfflineOperationRowProps {
  busy: boolean;
  operation: OfflineQueueRecord;
  onAction: (action: QueueAction, operation: OfflineQueueRecord) => void;
}

function operationTitle(operation: OfflineQueueRecord): string {
  return `${operation.entity.replaceAll('_', ' ')} ${operation.operation}`;
}

function procedureName(operation: OfflineQueueRecord): string {
  const payload = operation.payload as { procedure?: unknown };
  return typeof payload.procedure === 'string' ? payload.procedure : operation.idempotencyKey;
}

function operationTime(operation: OfflineQueueRecord): string {
  return formatDateTime(operation.updatedAt);
}

function nextRetryLabel(operation: OfflineQueueRecord): string | null {
  if (!operation.nextRetryAt || operation.status !== 'failed') return null;
  return formatTime(operation.nextRetryAt);
}

function statusClass(status: OfflineQueueRecord['status']): string {
  if (status === 'conflict') return 'bg-amber-100 text-amber-800';
  if (status === 'failed') return 'bg-destructive/10 text-destructive';
  if (status === 'syncing') return 'bg-blue-100 text-blue-800';
  return 'bg-muted text-muted-foreground';
}

function OfflineOperationRow({ busy, operation, onAction }: OfflineOperationRowProps) {
  const canApprove = operation.status === 'conflict';
  const canRetry = operation.status === 'failed' || operation.status === 'queued';

  return (
    <li className="space-y-2 border-b border-border py-2 last:border-b-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-medium capitalize">{operationTitle(operation)}</p>
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${statusClass(operation.status)}`}>
              {operation.status}
            </span>
          </div>
          <p className="truncate text-xs text-muted-foreground">{procedureName(operation)}</p>
        </div>
        <span className="shrink-0 text-[11px] text-muted-foreground">{operationTime(operation)}</span>
      </div>

      {operation.lastError && (
        <div className="flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">
            {displayErrorMessage(operation.lastError)}
            {nextRetryLabel(operation) ? ` Retry after ${nextRetryLabel(operation)}.` : ''}
          </span>
        </div>
      )}

      <div className="flex justify-end gap-1">
        {canApprove && (
          <button
            type="button"
            title="Approve for cloud sync"
            aria-label="Approve for cloud sync"
            disabled={busy}
            onClick={() => onAction('approve', operation)}
            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted disabled:opacity-50"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </button>
        )}
        {canRetry && (
          <button
            type="button"
            title="Retry sync"
            aria-label="Retry sync"
            disabled={busy}
            onClick={() => onAction('retry', operation)}
            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          title="Discard local operation"
          aria-label="Discard local operation"
          disabled={busy}
          onClick={() => onAction('discard', operation)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

export function OfflineQueuePanel({ children, isOnline }: OfflineQueuePanelProps) {
  const [open, setOpen] = useState(false);
  const [operations, setOperations] = useState<OfflineQueueRecord[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    listOfflineQueuedOperations(['queued', 'syncing', 'failed', 'conflict'])
      .then(setOperations)
      .catch(() => setOperations([]));
  }, []);

  useEffect(() => {
    refresh();
    return subscribeToOfflineStore(refresh);
  }, [refresh]);

  const runSync = useCallback(async () => {
    const summary = await syncNavioBuildOfflineQueue();
    if (summary.synced > 0) toast.success(`Synced ${summary.synced} offline change(s)`);
    if (summary.failed > 0 || summary.conflicts > 0) toast.error('Some offline changes need review');
    refresh();
  }, [refresh]);

  const runAction = useCallback(
    async (action: QueueAction, operation: OfflineQueueRecord) => {
      setBusyId(operation.id);
      try {
        if (action === 'approve') await approveOfflineOperationForSync(operation.id, getOfflineActorUserId());
        if (action === 'retry') await retryOfflineOperation(operation.id);
        if (action === 'discard') await removeOfflineOperation(operation.id);
        if (isOnline && action !== 'discard') await runSync();
        else refresh();
      } finally {
        setBusyId(null);
      }
    },
    [isOnline, refresh, runSync]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-[390px] p-3">
        <div className="mb-2 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Offline sync</p>
            <p className="text-xs text-muted-foreground">{operations.length} pending change(s)</p>
          </div>
          <button
            type="button"
            title="Sync now"
            aria-label="Sync now"
            disabled={!isOnline || busyId !== null}
            onClick={() => void runSync()}
            className="flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {operations.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            No pending offline changes
          </div>
        ) : (
          <ul className="max-h-[360px] overflow-auto">
            {operations.map((operation) => (
              <OfflineOperationRow
                key={operation.id}
                busy={busyId === operation.id}
                operation={operation}
                onAction={(action, item) => void runAction(action, item)}
              />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
